import { useState, useCallback, useEffect } from 'react';
import { DailyTask, StickyNoteState, UseStickyNoteResult } from '@/types';

const STORAGE_KEY = 'daily_sticky_note';
const DATE_KEY = 'sticky_note_date';
const POSITION_KEY = 'sticky_note_position';
const MAX_TASKS = 3;

// 生成唯一ID
const generateId = () => Date.now().toString(36) + Math.random().toString(36).substr(2);

// 获取今天的日期字符串
const getTodayString = () => new Date().toDateString();

// 检查是否是新的一天
const isNewDay = (): boolean => {
  const today = getTodayString();
  const lastDate = localStorage.getItem(DATE_KEY);
  return today !== lastDate;
};

// 默认位置（右上角）
const getDefaultPosition = () => ({
  x: Math.max(0, window.innerWidth - 320), // 便签宽度约300px + 边距
  y: 20
});

// 验证位置是否在可见区域内
const validatePosition = (position: { x: number; y: number }) => {
  const maxX = window.innerWidth - 320; // 便签宽度
  const maxY = window.innerHeight - 200; // 便签最小高度
  
  return {
    x: Math.max(0, Math.min(position.x, Math.max(0, maxX))),
    y: Math.max(0, Math.min(position.y, Math.max(0, maxY)))
  };
};

export const useStickyNote = (): UseStickyNoteResult => {
  // 初始化状态
  const [state, setState] = useState<StickyNoteState>(() => {
    // 检查是否需要重置（新的一天）
    if (isNewDay()) {
      // 清除旧数据
      localStorage.removeItem(STORAGE_KEY);
      localStorage.setItem(DATE_KEY, getTodayString());
    }

    // 从localStorage恢复状态
    const savedTasks = localStorage.getItem(STORAGE_KEY);
    const savedPosition = localStorage.getItem(POSITION_KEY);
    
    let tasks: DailyTask[] = [];
    if (savedTasks) {
      try {
        const parsed = JSON.parse(savedTasks);
        tasks = parsed.map((task: any) => ({
          ...task,
          createdAt: new Date(task.createdAt)
        }));
      } catch {
        // 解析失败，使用空数组
      }
    }

    let position = getDefaultPosition();
    if (savedPosition) {
      try {
        const parsedPosition = JSON.parse(savedPosition);
        // 验证位置是否在可见区域内
        position = validatePosition(parsedPosition);
      } catch {
        // 解析失败，使用默认位置
      }
    }

    return {
      tasks,
      position,
      isVisible: true,
      isMinimized: false,
      isEditing: null
    };
  });

  // 保存任务到localStorage
  const saveTasks = useCallback((tasks: DailyTask[]) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tasks));
    localStorage.setItem(DATE_KEY, getTodayString());
  }, []);

  // 保存位置到localStorage
  const savePosition = useCallback((position: { x: number; y: number }) => {
    localStorage.setItem(POSITION_KEY, JSON.stringify(position));
  }, []);

  // 添加任务
  const addTask = useCallback((content: string) => {
    setState(prev => {
      if (prev.tasks.length >= MAX_TASKS || !content.trim()) {
        return prev;
      }

      const newTask: DailyTask = {
        id: generateId(),
        content: content.trim(),
        completed: false,
        createdAt: new Date()
      };

      const newTasks = [...prev.tasks, newTask];
      saveTasks(newTasks);
      
      return {
        ...prev,
        tasks: newTasks,
        isEditing: newTask.id
      };
    });
  }, [saveTasks]);

  // 更新任务内容
  const updateTask = useCallback((id: string, content: string) => {
    setState(prev => {
      const newTasks = prev.tasks.map(task =>
        task.id === id ? { ...task, content: content.trim() } : task
      );
      saveTasks(newTasks);
      
      return {
        ...prev,
        tasks: newTasks,
        isEditing: null
      };
    });
  }, [saveTasks]);

  // 切换任务完成状态
  const toggleTask = useCallback((id: string) => {
    setState(prev => {
      const newTasks = prev.tasks.map(task =>
        task.id === id ? { ...task, completed: !task.completed } : task
      );
      saveTasks(newTasks);
      
      return {
        ...prev,
        tasks: newTasks
      };
    });
  }, [saveTasks]);

  // 删除任务
  const deleteTask = useCallback((id: string) => {
    setState(prev => {
      const newTasks = prev.tasks.filter(task => task.id !== id);
      saveTasks(newTasks);
      
      return {
        ...prev,
        tasks: newTasks,
        isEditing: prev.isEditing === id ? null : prev.isEditing
      };
    });
  }, [saveTasks]);

  // 更新便签位置
  const updatePosition = useCallback((position: { x: number; y: number }) => {
    const validatedPosition = validatePosition(position);
    setState(prev => ({ ...prev, position: validatedPosition }));
    savePosition(validatedPosition);
  }, [savePosition]);

  // 切换可见性
  const toggleVisibility = useCallback(() => {
    setState(prev => ({ ...prev, isVisible: !prev.isVisible }));
  }, []);

  // 切换最小化状态
  const toggleMinimize = useCallback(() => {
    setState(prev => ({ ...prev, isMinimized: !prev.isMinimized }));
  }, []);

  // 开始编辑
  const startEditing = useCallback((id: string) => {
    setState(prev => ({ ...prev, isEditing: id }));
  }, []);

  // 停止编辑
  const stopEditing = useCallback(() => {
    setState(prev => ({ ...prev, isEditing: null }));
  }, []);

  // 重置所有任务
  const resetTasks = useCallback(() => {
    setState(prev => {
      const newState = { ...prev, tasks: [], isEditing: null };
      saveTasks([]);
      return newState;
    });
  }, [saveTasks]);

  // 监听键盘事件
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // T键切换便签可见性
      if (event.key.toLowerCase() === 't' && !event.ctrlKey && !event.metaKey) {
        // 确保不在输入框内
        const activeElement = document.activeElement;
        if (activeElement?.tagName !== 'INPUT' && activeElement?.tagName !== 'TEXTAREA') {
          event.preventDefault();
          toggleVisibility();
        }
      }
      
      // Escape键停止编辑
      if (event.key === 'Escape' && state.isEditing) {
        stopEditing();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleVisibility, stopEditing, state.isEditing]);

  // 检查每日重置
  useEffect(() => {
    const checkDailyReset = () => {
      if (isNewDay()) {
        resetTasks();
        // 重新设置默认位置
        const defaultPosition = getDefaultPosition();
        updatePosition(defaultPosition);
      }
    };

    // 立即检查一次
    checkDailyReset();

    // 设置定时检查（每分钟检查一次）
    const interval = setInterval(checkDailyReset, 60000);
    return () => clearInterval(interval);
  }, [resetTasks, updatePosition]);

  // 监听窗口大小变化，调整位置
  useEffect(() => {
    const handleResize = () => {
      setState(prev => {
        const validatedPosition = validatePosition(prev.position);
        
        if (validatedPosition.x !== prev.position.x || validatedPosition.y !== prev.position.y) {
          savePosition(validatedPosition);
          return { ...prev, position: validatedPosition };
        }
        
        return prev;
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [savePosition]);

  return {
    state,
    actions: {
      addTask,
      updateTask,
      toggleTask,
      deleteTask,
      updatePosition,
      toggleVisibility,
      toggleMinimize,
      startEditing,
      stopEditing,
      resetTasks
    }
  };
};